import type { LocalDate } from '@cadencia/contracts';
import { daysBetweenLocal } from '../units/local-date.js';
import { roundTo } from '../units/round.js';

/**
 * Tendencia del peso corporal (docs/NUTRITION_ENGINE.md §2.3 y §11).
 *
 * La decision NUNCA se toma sobre dos pesos sueltos: se suaviza con una media
 * movil y se ajusta una recta sobre ella. Un peso diario se mueve un kilo por
 * el agua y el glucogeno; la diferencia entre el lunes y el lunes siguiente es
 * ruido con forma de dato.
 */

export interface WeightPoint {
  measuredOn: LocalDate;
  weightKg: number;
}

export interface SmoothedPoint {
  measuredOn: LocalDate;
  /** Media de la ventana que termina en este dia. */
  averageKg: number;
  /** Cuantas medidas reales entran en la media. */
  samples: number;
  /**
   * Si la ventana esta llena, es decir, si hay `windowDays` de historia por
   * detras. Los primeros puntos de una serie promedian menos dias y por eso su
   * media esta sesgada hacia el principio de la serie.
   *
   * Se mide en DIAS de historia y no en numero de medidas: quien se pesa cinco
   * dias por semana no llega nunca a siete muestras en siete dias, y su ventana
   * esta igual de llena que la de quien se pesa a diario.
   */
  windowComplete: boolean;
}

export const WEIGHT_TREND = {
  /** Ventana de la media movil, en dias. */
  windowDays: 7,
  /** Minimo de puntos suavizados para ajustar una recta con sentido. */
  minPointsForTrend: 7,
  /**
   * Pesadas por semana por debajo de las cuales la tendencia se dibuja pero se
   * marca como POCO FIABLE.
   *
   * Vivia en `CALIBRATION_RULES.minWeightsPerWeek`, dentro del motor de energia
   * de la nutricion, y se rescato aqui el 21/09/2026: es un umbral sobre la
   * MEDIDA —cuantas pesadas hacen falta para fiarse de una pendiente—, no sobre
   * las calorias. El §10 del encargo lo pide expresamente: distinguir la
   * medicion de la estimacion y llevar su error consigo.
   *
   * Cinco de siete. El peso corporal oscila por hidratacion, sal, glucogeno y
   * hora del dia mas de lo que se mueve la grasa en una semana, asi que con dos
   * o tres puntos la recta describe el ruido. No se sube a siete porque exigir
   * pesarse todos los dias para ver una tendencia convierte la funcion en
   * inalcanzable, y el §5 dice que el peso es opcional.
   */
  minWeightsPerWeekForReliableTrend: 5,
} as const;

/** Media movil hacia atras de `windowDays`, un punto por medida disponible. */
export function movingAverage(
  points: readonly WeightPoint[],
  windowDays: number = WEIGHT_TREND.windowDays,
): SmoothedPoint[] {
  const ordenados = [...points].sort((a, b) => (a.measuredOn < b.measuredOn ? -1 : 1));
  const primero = ordenados[0];
  return ordenados.map((punto, indice) => {
    const ventana = ordenados
      .slice(0, indice + 1)
      .filter((otro) => daysBetweenLocal(otro.measuredOn, punto.measuredOn) < windowDays);
    const suma = ventana.reduce((total, entrada) => total + entrada.weightKg, 0);
    return {
      measuredOn: punto.measuredOn,
      averageKg: roundTo(suma / ventana.length, 3),
      samples: ventana.length,
      windowComplete:
        primero !== undefined &&
        daysBetweenLocal(primero.measuredOn, punto.measuredOn) >= windowDays - 1,
    };
  });
}

export interface WeightTrend {
  /** Pendiente en kg/dia. Negativa si baja. */
  slopeKgPerDay: number;
  /** Error estandar de la pendiente, para la banda del §11. */
  standardErrorKgPerDay: number;
  interceptKg: number;
  daysCovered: number;
  measurements: number;
  /** Medidas por semana en el rango cubierto: senal de calidad del registro. */
  measurementsPerWeek: number;
}

/**
 * Ajusta una recta por minimos cuadrados sobre la media movil.
 *
 * El error estandar se calcula sobre los residuos de la serie **suavizada**, y
 * por eso sale mas pequeno que el de los pesos crudos: la banda que produce es
 * la de la tendencia, no la del peso de un dia concreto. Se dice aqui porque en
 * una grafica las dos bandas se parecen y no significan lo mismo.
 */
export function weightTrend(points: readonly WeightPoint[]): WeightTrend | null {
  // Solo los puntos con la ventana llena. Los primeros de la serie promedian
  // menos dias y arrastran la media hacia el principio, asi que meterlos en el
  // ajuste APLANA la pendiente: sobre una bajada perfecta de 100 g/dia durante
  // tres semanas salia -88,6 g/dia, un 11 % menos, y un error estandar que era
  // puro artefacto del arranque. No es un decimal: `calibrateTdee` multiplica
  // la pendiente por 7.700 kcal/kg, asi que ese 11 % son ~88 kcal/dia de sesgo
  // sistematico, y siempre en la direccion de «adelgazas menos de lo que crees».
  //
  // El precio es que hacen falta 13 dias de registro diario para tener siete
  // puntos utiles, en vez de siete. Encaja con `CALIBRATION_RULES.minDays`, que
  // ya pedia catorce.
  const suavizados = movingAverage(points).filter((punto) => punto.windowComplete);
  if (suavizados.length < WEIGHT_TREND.minPointsForTrend) return null;

  const primero = suavizados[0]!;
  const xs = suavizados.map((punto) => daysBetweenLocal(primero.measuredOn, punto.measuredOn));
  const ys = suavizados.map((punto) => punto.averageKg);
  const n = xs.length;
  const mediaX = xs.reduce((a, b) => a + b, 0) / n;
  const mediaY = ys.reduce((a, b) => a + b, 0) / n;

  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i]! - mediaX;
    sxx += dx * dx;
    sxy += dx * (ys[i]! - mediaY);
  }
  // Todas las medidas el mismo dia: no hay recta que ajustar.
  if (sxx === 0) return null;

  const slope = sxy / sxx;
  const intercept = mediaY - slope * mediaX;

  let sse = 0;
  for (let i = 0; i < n; i += 1) {
    const estimado = intercept + slope * xs[i]!;
    sse += (ys[i]! - estimado) ** 2;
  }
  const varianzaResidual = n > 2 ? sse / (n - 2) : 0;
  const standardError = Math.sqrt(varianzaResidual / sxx);

  const daysCovered = xs[n - 1]! + 1;
  // Las medidas del tramo AJUSTADO, no las de toda la serie: la calidad del
  // registro que interesa es la del periodo del que habla la tendencia.
  const enTramo = points.filter((punto) => punto.measuredOn >= primero.measuredOn).length;

  return {
    slopeKgPerDay: roundTo(slope, 5),
    standardErrorKgPerDay: roundTo(standardError, 5),
    interceptKg: roundTo(intercept, 3),
    daysCovered,
    measurements: enTramo,
    measurementsPerWeek: roundTo((enTramo / daysCovered) * 7, 2),
  };
}

export interface Projection {
  weeksAhead: number;
  weightKg: number;
  lowKg: number;
  highKg: number;
}

export const MAX_PROJECTION_WEEKS = 8;

/**
 * Proyeccion con banda (§11). Nunca mas de 8 semanas: mas alla, la propia
 * pendiente habra cambiado con el peso y la proyeccion seria una raya bonita
 * sin nada detras.
 *
 * La banda es `1,96 × SE_pendiente × dias`. El §11 escribia `× √t`, que no
 * cuadra en unidades: el error estandar de la pendiente esta en kg/dia, asi que
 * para leerlo en kilos hay que multiplicarlo por el horizonte, no por su raiz.
 * Con `√t` la banda se estrecharia al alejarse, que es justo al reves de lo que
 * pasa al extrapolar. El documento se ha corregido (ADR-039).
 */
export function projectWeight(
  trend: WeightTrend,
  currentKg: number,
  weeksAhead: number,
): Projection {
  if (weeksAhead < 0) throw new Error('Horizonte negativo');
  const semanas = Math.min(weeksAhead, MAX_PROJECTION_WEEKS);
  const dias = semanas * 7;
  const centro = currentKg + trend.slopeKgPerDay * dias;
  const banda = 1.96 * trend.standardErrorKgPerDay * dias;
  return {
    weeksAhead: semanas,
    weightKg: roundTo(centro, 2),
    lowKg: roundTo(centro - banda, 2),
    highKg: roundTo(centro + banda, 2),
  };
}
